import { useMutation, useQuery } from "convex/react";
import { X } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { Composer } from "./Composer";
import { MessageItem } from "./MessageItem";
import { conversationLabel, type Conversation, type MeReady, type Participant } from "./types";

type Props = {
  me: MeReady;
  conversation: Conversation;
  rootId: string;
  mentionables: readonly Participant[];
  mentionNames: readonly string[];
  onClose: () => void;
};

export function ThreadPanel({ me, conversation, rootId, mentionables, mentionNames, onClose }: Props) {
  const root = useQuery(api.communication.getMessage, { messageId: rootId });
  const replies = useQuery(api.communication.listMessages, { conversationId: conversation.id, threadRootId: rootId, limit: 200 });
  const toggleReaction = useMutation(api.communication.toggleReaction);
  const editMessage = useMutation(api.communication.editMessage);
  const label = conversationLabel(conversation);

  const item = (message: NonNullable<typeof root>) => (
    <MessageItem
      key={message.id}
      message={message}
      inThread
      mentionNames={mentionNames}
      canEdit={message.authorPrincipalId === me.principalId && message.deletedAt === undefined}
      canHideForEveryone={me.role === "owner" || me.role === "admin"}
      onReact={(emoji) => { void toggleReaction({ messageId: message.id, emoji }); }}
      onEdit={(plainText) => editMessage({ messageId: message.id, plainText })}
    />
  );

  return (
    <aside className="thread-panel" aria-label="Thread">
      <header>
        <div><strong>Thread</strong><span>{conversation.type === "direct" ? label : `#${label}`}</span></div>
        <Button size="icon" variant="ghost" aria-label="Close thread" onClick={onClose}><X size={16} /></Button>
      </header>
      <div className="thread-content">
        {root === undefined ? <p className="loading-hint">Loading…</p> : root === null ? <p className="loading-hint">This message is no longer available.</p> : item(root)}
        {replies !== undefined && replies.page.length > 0 ? <div className="thread-divider">{replies.page.length} {replies.page.length === 1 ? "reply" : "replies"}</div> : null}
        {(replies?.page ?? []).map((reply) => item(reply))}
      </div>
      <Composer conversation={conversation} mentionables={mentionables} placeholder="Reply in thread…" threadRootId={rootId} compact autoFocus />
    </aside>
  );
}
